import {
  Box, Container, Typography, Button, Grid,
  Card, CardContent, Chip, Stack, alpha,
} from '@mui/material'
import { Link } from 'react-router-dom'
import {
  IconDroplet, IconChartBar, IconMap2, IconBulb,
  IconArrowRight, IconSeedling, IconUsers, IconReportAnalytics,
} from '@tabler/icons-react'
import { tokens } from '@/theme'
import NewsletterSignup from '@/components/NewsletterSignup'

// ─── Data ─────────────────────────────────────────────────────────────────────
const FEATURES = [
  {
    icon: <IconDroplet size={28} stroke={1.5} />,
    title: 'Groundwater Monitoring',
    body: 'Basin-level data from CASGEM and local agencies, organized into readable summaries.',
  },
  {
    icon: <IconChartBar size={28} stroke={1.5} />,
    title: 'Reporting & Visibility',
    body: 'Turn fragmented water-use and field data into structured reports your team can actually use.',
  },
  {
    icon: <IconMap2 size={28} stroke={1.5} />,
    title: 'GSA & SGMA Tracking',
    body: 'Stay current on GSA meetings, Groundwater Sustainability Plan updates, and basin-specific news.',
  },
  {
    icon: <IconBulb size={28} stroke={1.5} />,
    title: 'Irrigation Intelligence',
    body: 'ET estimates, soil conditions, and crop water demand data alongside your groundwater picture.',
  },
]

const AUDIENCES = [
  {
    icon: <IconSeedling size={22} stroke={1.5} />,
    role: 'Growers & Farm Operators',
    message: 'Stay ahead of groundwater changes and irrigation reporting without reading every agency document.',
  },
  {
    icon: <IconUsers size={22} stroke={1.5} />,
    role: 'GSAs & Water Agencies',
    message: 'Organize and communicate water-related data so your stakeholders understand what\'s happening.',
  },
  {
    icon: <IconReportAnalytics size={22} stroke={1.5} />,
    role: 'Agricultural Consultants',
    message: 'Better context for client reporting — aggregated groundwater, irrigation, and policy updates.',
  },
]

const STATS = [
  { value: '500+', label: 'California GSA basins tracked' },
  { value: '3×',  label: 'Faster data aggregation'      },
  { value: '100%', label: 'Public data sources'          },
]

// ─── Component ────────────────────────────────────────────────────────────────
export default function Home() {
  return (
    <>
      {/* ── Hero ── */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${tokens.green['900']} 0%, ${tokens.green.dark} 60%, ${tokens.blue.dark} 100%)`,
          color: '#fff',
          pt: { xs: 10, md: 14 },
          pb: { xs: 8, md: 12 },
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Subtle grid texture */}
        <Box
          sx={{
            position: 'absolute', inset: 0,
            backgroundImage: `radial-gradient(${alpha('#fff', 0.04)} 1px, transparent 1px)`,
            backgroundSize: '32px 32px',
            pointerEvents: 'none',
          }}
        />
        <Container maxWidth="md" sx={{ position: 'relative' }}>
          <Chip
            label="Agriculture · Water · Data"
            size="small"
            sx={{
              fontFamily: '"Poppins", system-ui, sans-serif',
              fontSize: '0.68rem',
              letterSpacing: '0.12em',
              background: alpha('#fff', 0.1),
              color: 'rgba(255,255,255,0.75)',
              border: `1px solid ${alpha('#fff', 0.2)}`,
              mb: 3,
            }}
          />
          <Typography variant="h1" sx={{ fontSize: { xs: '2.8rem', md: '4.5rem' }, color: '#fff', mb: 2.5 }}>
            Better Data.<br />Better Decisions.
          </Typography>
          <Typography
            variant="body1"
            sx={{ opacity: 0.8, maxWidth: 560, mx: 'auto', mb: 4, fontSize: '1.1rem' }}
          >
            AgroLense helps growers, GSAs, and agricultural consultants stay informed through data aggregation, reporting, and clear insights across groundwater, irrigation, and water management.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ justifyContent: 'center' }}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              component={Link}
              to="/news"
              endIcon={<IconArrowRight size={18} />}
              sx={{ background: '#fff !important', color: `${tokens.green.dark} !important` }}
            >
              Explore Updates
            </Button>
            <Button
              variant="outlined"
              size="large"
              component={Link}
              to="/gsa-california"
              sx={{
                borderColor: '#fff !important',
                borderWidth: '2px !important',
                color: '#fff !important',
                '&:hover': { background: alpha('#fff', 0.1) },
              }}
            >
              GSA Resources
            </Button>
          </Stack>
        </Container>
      </Box>

      {/* ── Stats strip ── */}
      <Box sx={{ background: tokens.green.main, color: '#fff' }}>
        <Container maxWidth="md">
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
            }}
          >
            {STATS.map(({ value, label }) => (
              <Box
                key={label}
                sx={{
                  py: 3, px: 4,
                  textAlign: 'center',
                  borderRight: { sm: `1px solid ${alpha('#fff', 0.15)}` },
                  '&:last-child': { borderRight: 'none' },
                }}
              >
                <Typography
                  sx={{
                    fontFamily: '"Poppins", system-ui, sans-serif',
                    fontWeight: 900,
                    fontSize: '2rem',
                    lineHeight: 1,
                    mb: 0.5,
                  }}
                >
                  {value}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.75, letterSpacing: '0.06em' }}>
                  {label.toUpperCase()}
                </Typography>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* ── Features ── */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        <Box sx={{ textAlign: 'center', mb: 7 }}>
          <Typography variant="overline" sx={{ color: tokens.green.main, display: 'block', mb: 1 }}>
            What AgroLense Does
          </Typography>
          <Typography variant="h2" sx={{ fontSize: { xs: '1.9rem', md: '2.6rem' }, mb: 1.5 }}>
            Organized Data. Clearer Picture.
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: 540, mx: 'auto' }}>
            Agriculture and water information is often scattered across dozens of sources. AgroLense brings it together.
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {FEATURES.map(({ icon, title, body }) => (
            <Grid key={title} size={{ xs: 12, sm: 6, md: 3 }}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ p: 3 }}>
                  <Box
                    sx={{
                      display: 'inline-flex',
                      p: 1.25,
                      borderRadius: 1.5,
                      background: alpha(tokens.green.main, 0.08),
                      color: tokens.green.main,
                      mb: 2,
                    }}
                  >
                    {icon}
                  </Box>
                  <Typography variant="h6" sx={{ mb: 1 }}>{title}</Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>{body}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* ── Audiences ── */}
      <Box sx={{ background: tokens.green['50'], py: { xs: 8, md: 10 } }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="overline" sx={{ color: tokens.green.main, display: 'block', mb: 1 }}>
              Who We Serve
            </Typography>
            <Typography variant="h2" sx={{ fontSize: { xs: '1.9rem', md: '2.4rem' } }}>
              Built for the Agricultural Ecosystem
            </Typography>
          </Box>
          <Grid container spacing={3} sx={{ justifyContent: 'center' }}>
            {AUDIENCES.map(({ icon, role, message }) => (
              <Grid key={role} size={{ xs: 12, md: 4 }}>
                <Card sx={{ height: '100%', background: '#fff' }}>
                  <CardContent sx={{ p: 3.5 }}>
                    <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', mb: 2 }}>
                      <Box sx={{ color: tokens.blue.main }}>{icon}</Box>
                      <Typography
                        variant="caption"
                        sx={{
                          fontFamily: '"Poppins", system-ui, sans-serif',
                          color: tokens.blue.main,
                          letterSpacing: '0.08em',
                          textTransform: 'uppercase',
                        }}
                      >
                        {role}
                      </Typography>
                    </Stack>
                    <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                      {message}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── Newsletter signup ── */}
      <NewsletterSignup />

      {/* ── CTA ── */}
      <Box
        sx={{
          background: tokens.blue.dark,
          color: '#fff',
          py: { xs: 8, md: 10 },
          textAlign: 'center',
        }}
      >
        <Container maxWidth="sm">
          <Typography variant="h2" sx={{ color: '#fff', mb: 2, fontSize: { xs: '2rem', md: '2.8rem' } }}>
            Stay Informed
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.8, mb: 4 }}>
            Monthly updates on groundwater, GSA activity, and irrigation data for California's agricultural communities.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ justifyContent: 'center' }}>
            <Button
              variant="contained"
              size="large"
              component={Link}
              to="/news"
              endIcon={<IconArrowRight size={18} />}
              sx={{ background: '#fff !important', color: `${tokens.blue.dark} !important` }}
            >
              Browse Updates
            </Button>
            <Button
              variant="outlined"
              size="large"
              component={Link}
              to="/gsa-california"
              sx={{
                borderColor: '#fff !important',
                borderWidth: '2px !important',
                color: '#fff !important',
                '&:hover': { background: alpha('#fff', 0.1) },
              }}
            >
              GSA Resources
            </Button>
          </Stack>
        </Container>
      </Box>
    </>
  )
}
